import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { httpService } from '@/api/v1/http.service'
import DEFAULT_PAGE_SIZE from '@/constants/default-page-size'
import type { PaginationParams } from '@/types/params'
import type { DeepCamelCase } from '@/types/utils'
import { idAndNameSchema, requiredString } from '@/utils/zod'

type APIResponse = z.infer<typeof responseSchema>
export type CoursesList = DeepCamelCase<APIResponse>
export type CoursesListItem = CoursesList[number]

const responseSchema = z.array(
  z.object({
    id: requiredString(),
    name: requiredString(),
    display_name: requiredString(),
    education_level: idAndNameSchema,
    domain: idAndNameSchema,
    preferred_rooms: z.array(idAndNameSchema).nullable(),
    can_be_deleted: z.boolean()
  })
)

const parseResponse = (response: APIResponse): CoursesList =>
  response.map(item => ({
    id: item.id,
    name: item.name,
    displayName: item.display_name,
    educationLevel: item.education_level,
    domain: item.domain,
    preferredRooms: item.preferred_rooms,
    canBeDeleted: item.can_be_deleted
  }))

type Filters = {
  ids?: string[]
  educationLevelIds?: string[]
  domainIds?: string[]
  preferredRoomIds?: string[]
}

type Params = Filters & PaginationParams

export const useCourses = (params: Params = {}) =>
  useQuery({
    queryKey: ['panelCoursesManagementList', params],
    staleTime: 60 * 1000,
    queryFn: async () => {
      const response = await httpService.panel.panelCoursesList({
        page: params.page ?? 1,
        page_size: params.pageSize ?? DEFAULT_PAGE_SIZE,
        id: params.ids,
        education_level_id: params.educationLevelIds,
        domain_id: params.domainIds,
        preferred_room_id: params.preferredRoomIds,
        fetchKeys: {
          id: true,
          name: true,
          display_name: true,
          education_level: true,
          domain: true,
          preferred_rooms: true,
          can_be_deleted: true
        }
      })

      const parsed = parseResponse(responseSchema.parse(response.results))
      return { list: parsed, count: response.count }
    }
  })
